// Token and band value grammars (E01-S1-T2, decided in D-03). This is the ONE file that states
// either pattern; nothing else in the repository may restate them.
import { z } from "zod";
/** Entity kinds an identifier field may declare (D-09); each has a token prefix. */
export const EntityKind = z.enum(["case", "account", "security"]);
export const TOKEN_PREFIXES = ["CASE", "ACCT", "SEC", "ID"];
/** Crockford base32 without I, L, O, U. */
export const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
/**
 * A token is random, never a hash of the value: `<KIND>-<8 Crockford base32 characters>`.
 * CASE, ACCT and SEC for the three entity kinds; ID for an identifier field with no entity.
 */
export const TOKEN_RE = /^(CASE|ACCT|SEC|ID)-[0-9A-HJKMNP-TV-Z]{8}$/;
export const TokenSchema = z.string().regex(TOKEN_RE, "expected a session token: KIND-XXXXXXXX");
/** The prefix a token carries for an identifier field: its entity kind, or ID when it has none. */
export function tokenPrefixFor(entity) {
    switch (entity) {
        case "case":
            return "CASE";
        case "account":
            return "ACCT";
        case "security":
            return "SEC";
        default:
            return "ID";
    }
}
/**
 * A band label: `<1k`, `1k–10k`, `50k–250k`, `250k+`, `1M–5M`. Always an en dash, always a
 * k or M suffix, never a plain figure — which is what lets a schema tell a band from a value.
 */
// One to three digits, at most two decimals: a figure such as 41206.18 cannot wear a k suffix and pass.
const UNIT = String.raw `\d{1,3}(?:\.\d{1,2})?[kM]`;
export const BAND_RE = new RegExp(`^(?:<${UNIT}|${UNIT}–${UNIT}|${UNIT}\\+)$`);
export const BandSchema = z.string().regex(BAND_RE, "expected a band label such as 10k–50k");
/** 1000 → "1k", 250000 → "250k", 1500000 → "1.5M". Thresholds below 1000 are not supported. */
export function formatThreshold(n) {
    if (!Number.isFinite(n) || n < 1000)
        throw new RangeError("band thresholds start at 1000");
    const [value, unit] = n >= 1_000_000 ? [n / 1_000_000, "M"] : [n / 1000, "k"];
    const text = Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));
    return `${text}${unit}`;
}
/** The labels an ascending threshold list produces, in bandIndex order. */
export function bandLabels(thresholds) {
    const first = thresholds[0];
    const last = thresholds[thresholds.length - 1];
    if (first === undefined || last === undefined)
        throw new RangeError("at least one threshold");
    for (let i = 1; i < thresholds.length; i += 1) {
        if ((thresholds[i] ?? 0) <= (thresholds[i - 1] ?? 0))
            throw new RangeError("thresholds must be strictly increasing");
    }
    const labels = [`<${formatThreshold(first)}`];
    for (let i = 1; i < thresholds.length; i += 1) {
        const lo = thresholds[i - 1];
        const hi = thresholds[i];
        if (lo === undefined || hi === undefined)
            throw new RangeError("thresholds must be strictly increasing");
        labels.push(`${formatThreshold(lo)}–${formatThreshold(hi)}`);
    }
    labels.push(`${formatThreshold(last)}+`);
    return labels;
}
/** The band a figure falls in. Taken on the absolute value; the sign is its own enum field. */
export function bandFor(value, thresholds) {
    const labels = bandLabels(thresholds);
    const abs = Math.abs(value);
    let index = thresholds.findIndex((t) => abs < t);
    if (index < 0)
        index = thresholds.length;
    const band = labels[index]; // bandLabels gives one label per threshold, plus one
    if (band === undefined)
        throw new RangeError("no band for the value");
    return { band, bandIndex: index };
}
/** The POC atlas's default thresholds; the mockups' five bands. */
export const DEFAULT_BANDS = [1000, 10_000, 50_000, 250_000];
//# sourceMappingURL=values.js.map